import { useEffect, useState, useMemo, useCallback } from 'react';
import { ConfigProvider } from 'antd';
import { UploadFileHandler } from '@components/Handlers/UploadFileHandler/Index.jsx';
import { CustomLoader } from '@components/UI/CustomLoader/Index.jsx';
import ptBR from 'antd/es/locale/pt_BR';
import { StyledTableItensProcessoSolicitacao } from '@styles/datatables/datatable-itens-processo-solicitacao.js';
import { BlockedOptionComp } from '@components/UI/BlockedOption/Index.jsx';
import SweetAlert from '@components/Feedback/SweetAlert/Index.jsx';
import { UpdateItensSelecionados } from '@services/SolicitacaoProcessoItem/Index.jsx';
import { AmostrasHandler } from '@components/Handlers/AmostrasHandler/Index.jsx';
import TextBlock from "@components/UI/TextBlock/Index..jsx";
import ObservacoesHandler from "@components/Handlers/ObservacoesHandler/Index.jsx";
import { useDispatch, useSelector } from 'react-redux';
import { carregarObservacoesIniciais, limparObservacoes } from '@store/slices/observacoesSlice.js'
import { carregarAmostra, limparAmostra } from '@store/slices/amostrasSlice.js';
import { useDisableIfStatusAndRole } from '@components/UI/DisableIfStatusAndRole/Index.jsx';
import { carregarFormularioAssociadoItemProcesso, limparFormularioAssociadoItemProcesso } from '@store/slices/formularios/ItemProcesso/formularioAssociadoItemProcessoSlice.js';
import { FormularioOptionItemProcessoModal } from '@components/Modals/Admin/FormularioOptionItemProcessoModal/Index.jsx';
import { carregarDocumentos, limparDocumentos } from '@store/slices/documentosSlice';
import { selectStatusById } from '@store/selectors/solicitacaoSelector';
import { perfisAdmin } from '@utils/auth/perfis';

const GridEditItensProcessoSolicitacao = ({
  solicitacaoProcessoItems = [],
  onUpdateSolicitacaoProcessoItems,
  idSolicitacao,
  idProcesso
}) => {

  const [loading, setLoading] = useState(false);
  const [sortedData, setSortedData] = useState([]);
  const dispatch = useDispatch();
  const statusSolicitacaoSelector = useCallback(selectStatusById(idSolicitacao), [idSolicitacao]);
  const statusSolicitacao = useSelector(statusSolicitacaoSelector);

  const blockSelectItems = useDisableIfStatusAndRole(idSolicitacao, [2, 3, 4, 5, 6, 7], perfisAdmin);

  const sortedInitialData = useMemo(() => {
    if (!solicitacaoProcessoItems.length) return [];
    return [...solicitacaoProcessoItems].sort((a, b) => {
      if (a.flagSelecionado && !b.flagSelecionado) return -1;
      if (!a.flagSelecionado && b.flagSelecionado) return 1;
      return a.nomeItem.localeCompare(b.nomeItem);
    });
  }, [solicitacaoProcessoItems]);

  useEffect(() => {
    if (!sortedInitialData.length) return;

    const novosDados =
      statusSolicitacao >= 2
        ? sortedInitialData.filter((item) => item.flagSelecionado)
        : sortedInitialData;

    const jaAtualizado = sortedData.length === novosDados.length &&
      sortedData.every((item, idx) => item.idProcessoItem === novosDados[idx].idProcessoItem);

    if (!jaAtualizado) {
      setSortedData(novosDados);
      onUpdateSolicitacaoProcessoItems(novosDados);
    }
  }, [statusSolicitacao, sortedInitialData]);


  useEffect(() => {
    if (solicitacaoProcessoItems.length > 0) {
      const observacoesParaRedux = solicitacaoProcessoItems.map(item => ({
        idProcessoItem: item.idProcessoItem,
        observacoes: item.observacoes || null,
      }));

      solicitacaoProcessoItems.forEach(item => {
        // Documentos
        if (item.documentos) {
          dispatch(carregarDocumentos({
            idProcessoItem: item.id,
            documentos: item.documentos
          }));
        }

        // Formulário
        if (item.formularioAssociadoItemProcesso) {
          dispatch(carregarFormularioAssociadoItemProcesso({
            idProcessoItem: item.id,
            formulario: item.formularioAssociadoItemProcesso
          }));
        }

        // Amostras
        if (item.amostras) {
          dispatch(carregarAmostra({
            idProcessoItem: item.id,
            amostras: item.amostras
          }));
        }

      });

      dispatch(carregarObservacoesIniciais(observacoesParaRedux));
    }
  }, [solicitacaoProcessoItems, dispatch]);

  const handleRowSelectionChange = useCallback(async (selectedRowKeys) => {

    if (selectedRowKeys.length === 0) {
      SweetAlert.error(
        "Seleção inválida",
        "Pelo menos um item do processo deve permanecer selecionado."
      );
      return;
    }
    // descobre se há desseleção
    const unselectedItems = sortedData.filter(
      (item) =>
        item.flagSelecionado && !selectedRowKeys.includes(item.idProcessoItem)
    );
    const itemDeselecionado =
      unselectedItems.length === 1 ? unselectedItems[0] : null;

    if (itemDeselecionado) {
      const confirmed = await SweetAlert.deselect(
        `Esta ação será irreversível e excluirá os formulários, documentos e amostras associados ao item do processo: <strong>${itemDeselecionado.nomeItem}</strong>`
      );
      if (!confirmed) return;
    }

    /* 1. Prepara versão futura, mas NÃO commitamos ainda  */
    const updated = sortedData.map((item) => {
      const isSelected = selectedRowKeys.includes(item.idProcessoItem);
      return {
        ...item,
        flagSelecionado: isSelected,
        observacoes: isSelected ? item.observacoes : null,
        formularioAssociadoItemProcesso: isSelected ? item.formularioAssociadoItemProcesso : null,
        documentos: isSelected ? item.documentos : [],
        amostras: isSelected ? item.amostras : null,
      };
    });

    // id alvo p/ API
    const previouslySelected = sortedData
      .filter(i => i.flagSelecionado)
      .map(i => i.idProcessoItem);
    const newlySelected = selectedRowKeys.find(id => !previouslySelected.includes(id));
    const idProcessoItemAlvo = itemDeselecionado?.idProcessoItem || newlySelected;

    try {
      SweetAlert.loading();

      await UpdateItensSelecionados(updated[0].idSolicitacao, {
        idProcessoItem: idProcessoItemAlvo,
        flagSelecionado: newlySelected !== undefined,
      });

      SweetAlert.close();       // fecha spinner

      /* 2. Agora sim commitamos estado/redux */
      setSortedData(updated);
      onUpdateSolicitacaoProcessoItems(updated);

      if (!newlySelected && itemDeselecionado) {
        // Limpeza só quando virou false
        dispatch(limparObservacoes(itemDeselecionado.id));
        dispatch(limparFormularioAssociadoItemProcesso(itemDeselecionado.id));
        dispatch(limparDocumentos(itemDeselecionado.id));
        dispatch(limparAmostra(itemDeselecionado.id));
      }

      // feedback visual
      if (newlySelected) {
        const itemSel = sortedData.find(i => i.idProcessoItem === newlySelected);
        if (itemSel)
          SweetAlert.success(
            'Item Selecionado',
            `Item "${itemSel.nomeItem}" selecionado com sucesso.`
          );
      } else if (itemDeselecionado) {
        SweetAlert.success(
          'Item Desselecionado',
          `Item "${itemDeselecionado.nomeItem}" desselecionado com sucesso.`
        );
      }
    } catch (error) {
      console.error('Erro ao sincronizar com a API:', error);
      SweetAlert.error('Erro', 'Não foi possível atualizar os itens selecionados.');
      /* Estado permanece intacto; usuário vê seleção antiga */
    }
  }, [sortedData, onUpdateSolicitacaoProcessoItems, dispatch]);

  const handleSelectOption = useCallback((idProcessoItem, value) => {
    const updated = sortedData.map((item) =>
      item.idProcessoItem === idProcessoItem
        ? { ...item, formulario: value }
        : item
    );
    setSortedData(updated);
    onUpdateSolicitacaoProcessoItems(updated);
  }, [sortedData, onUpdateSolicitacaoProcessoItems]);

  const columns = useMemo(() => [
    {
      title: 'Nome do Item',
      dataIndex: 'nomeItem',
      key: 'nomeItem',
      width: '43%',
      align: 'center',
      sorter: (a, b) => a.nomeItem.localeCompare(b.nomeItem),
      render: (text) => <TextBlock>{text}</TextBlock>,
    },
    {
      title: 'Observações',
      key: 'observacoes',
      width: '12%',
      render: (record) => {
        const nomeItemProcesso = record.nomeItem;

        return record.flagSelecionado ? (
          <div className="d-flex gap-2 justify-content-center">
            <ObservacoesHandler
              row={record}
              nomeItemProcesso={nomeItemProcesso}
            />
          </div>
        ) : (
          <BlockedOptionComp title={"Selecione o item para acessar"} />
        );
      },
    },
    {
      title: 'Formulário',
      key: 'formulario',
      width: '12%',
      render: (record) => {
        return record.flagSelecionado ? (
          <div className="d-flex gap-2 justify-content-center">
            <FormularioOptionItemProcessoModal
              idSolicitacaoProcessoItem={record.id}
              idProcesso={idProcesso}
              idSolicitacao={record.idSolicitacao}
              formularioRespostaInicial={record.formularioRespostaItemProcesso}
            />
          </div>
        ) : (
          <BlockedOptionComp title={"Selecione o item para acessar"} />
        );
      },
    },
    {
      title: 'Documentos',
      key: 'documentos',
      align: 'center',
      width: '12%',
      render: (record) => {

        return record.flagSelecionado ? (
          <div className="d-flex align-items-center gap-2 justify-content-center">
            <UploadFileHandler
              row={record}
            />
          </div>
        ) : (
          <BlockedOptionComp title={"Selecione o item para acessar"} />
        );
      },
    },
    {
      title: 'Amostras',
      key: 'amostras',
      align: 'center',
      width: '12%',
      render: (record) => {
        const nomeItemProcesso = record.nomeItem;
        const amostras = record.amostras || null;

        return record.flagSelecionado ? (
          <div className="d-flex gap-2 justify-content-center">
            <AmostrasHandler
              row={record}
              existingAmostra={amostras}
              nomeItemProcesso={nomeItemProcesso}
            />
          </div>
        ) : (
          <BlockedOptionComp title={"Selecione o item para acessar"} />
        );
      },
    },
  ], [sortedData, handleSelectOption]);

  const rowSelection = useMemo(() => ({
    columnTitle: '-',

    selectedRowKeys: sortedData
      .filter((item) => item.flagSelecionado)
      .map((item) => item.idProcessoItem),

    onChange: handleRowSelectionChange,
    preserveSelectedRowKeys: true,
    getCheckboxProps: () => ({
      disabled: blockSelectItems, // desabilita o checkbox de seleção
    }),
  }), [sortedData, handleRowSelectionChange, blockSelectItems]);

  const selectedCount = rowSelection.selectedRowKeys.length;

  return (
    <div className="mt-1">
      {loading && <CustomLoader />}
      <div className="mt-3 text-start">
        <h1>
          <strong>{selectedCount}</strong>{' '}
          {selectedCount === 1 ? 'item selecionado' : 'itens selecionados'}
        </h1>
      </div>
      <div className="mt-4">
        <ConfigProvider locale={ptBR}>
          <StyledTableItensProcessoSolicitacao
            rowKey="idProcessoItem"
            columns={columns}
            dataSource={sortedData}
            rowSelection={rowSelection}
            pagination={{
              defaultPageSize: 5,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50, 100],
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
            }}
            locale={{
              emptyText: <h3 className="m-3">Nenhum dado disponível para exibição.</h3>,
            }}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};

export default GridEditItensProcessoSolicitacao;